import { useState } from 'react';
import { useMe, useWebhookCategories, useWebhookMode, useWebhookStats } from '../../../api/queries';
import { useSaveWebhookCategory, useSetWebhookMode } from '../../../api/mutations';
import { Ic } from '../../../components/Ic';
import { Seg } from '../../../components/Seg';
import type { WebhookChannel } from '../../../api/types';

const CHANNELS: Array<{ id: WebhookChannel; label: string; blurb: string; color: string }> = [
  { id: 'main', label: '#main', blurb: 'Standard raffle alerts', color: 'var(--accent)' },
  { id: 'nsf', label: '#nsf', blurb: 'NSF stores (LootVault, Toymate, Pokebox, RAMint, PopMartAU)', color: 'var(--pink)' },
  { id: 'layout', label: '#layout', blurb: 'Copy-paste markdown helper', color: 'var(--teal)' },
  { id: 'sitemap', label: '#sitemap', blurb: 'New URL discoveries', color: 'var(--warn)' },
  { id: 'social', label: '#social', blurb: 'Instagram / Facebook detections', color: 'var(--orange)' },
];

export function WebhooksTab() {
  const { data: me } = useMe();
  const { data: modeInfo, isLoading } = useWebhookMode();
  const { data: stats } = useWebhookStats();
  const setMode = useSetWebhookMode();
  const { data: categories } = useWebhookCategories();
  const saveCategory = useSaveWebhookCategory();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [testUrl, setTestUrl] = useState('');
  const [prodUrl, setProdUrl] = useState('');

  const mode = modeInfo?.mode ?? 'unknown';
  const configured = modeInfo?.configured;

  return (
    <>
      <div className="pcard">
        <div className="head">
          <Ic.webhook className="ic" />
          <h3>Delivery mode</h3>
          <span className="spacer" />
          {mode !== 'unknown' && (
            <Seg
              value={mode}
              onChange={(next) => setMode.mutate(next)}
              options={[
                { value: 'test' as const, label: 'Test' },
                { value: 'prod' as const, label: 'Prod' },
              ]}
              ariaLabel="Webhook mode"
            />
          )}
        </div>
        <div className="body pad">
          <div className="muted" style={{ fontSize: 12.5 }}>
            {me?.is_super_admin
              ? 'Switches the active profile. Category URLs are encrypted at rest and never displayed after saving.'
              : 'Only a super admin can change the delivery mode.'}
          </div>
          {mode === 'unknown' && !isLoading && (
            <div className="err" style={{ marginTop: 10 }}>
              Webhook mode is unavailable — the worker config could not be read.
            </div>
          )}
        </div>
      </div>

      <div className="pcard">
        <div className="head"><Ic.webhook className="ic" /><h3>Delivery categories</h3><span className="spacer" /><span className="muted" style={{ fontSize: 11.5 }}>category default, scraper override</span></div>
        <div className="body">
          {(categories ?? []).map((category) => (
            <div key={category.slug} className="wh-row">
              <span className="sw" style={{ background: category.configured[mode === 'unknown' ? 'prod' : mode] ? 'var(--accent)' : 'var(--line-2)' }} />
              <div className="whmain"><div className="whname">{category.name}</div><div className="whurl">{category.slug} · test {category.configured.test ? 'configured' : 'not set'} · prod {category.configured.prod ? 'configured' : 'not set'}</div></div>
            </div>
          ))}
          {me?.is_super_admin && (
            <form className="body pad" style={{ borderTop: '1px solid var(--line-1)', display: 'grid', gap: 8 }} onSubmit={(e) => { e.preventDefault(); saveCategory.mutate({ slug, name, urls: { test: testUrl || null, prod: prodUrl || null } }); }}>
              <div className="muted" style={{ fontSize: 12.5 }}>Add or update a category. Leaving a URL blank clears that profile.</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input className="input" required placeholder="slug (e.g. sneakers)" value={slug} onChange={(e) => setSlug(e.target.value)} />
                <input className="input" required placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <input className="input" placeholder="Test webhook URL" value={testUrl} onChange={(e) => setTestUrl(e.target.value)} />
              <input className="input" placeholder="Production webhook URL" value={prodUrl} onChange={(e) => setProdUrl(e.target.value)} />
              <div><button className="btn sm" disabled={saveCategory.isPending}>Save category</button></div>
            </form>
          )}
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.zap className="ic" />
          <h3>Channels</h3>
          <span className="spacer" />
          {/* The design showed user-created webhooks with names and colour
              pickers. URLs are environment config, so this reflects what is
              actually configured rather than implying they're editable here. */}
          <span className="muted" style={{ fontSize: 11.5 }}>
            configured in the environment
          </span>
        </div>
        <div className="body">
          {CHANNELS.map((c) => {
            const count = stats?.counts?.[c.id];
            const inTest = configured?.test?.[c.id];
            const inProd = configured?.prod?.[c.id];
            const activeSet = mode === 'prod' ? inProd : inTest;
            return (
              <div key={c.id} className="wh-row">
                <span className="sw" style={{ background: activeSet ? c.color : 'var(--line-2)' }} />
                <div className="whmain">
                  <div className="whname">
                    {c.label}
                    {!activeSet && (
                      <span className="pill" style={{ marginLeft: 8 }}>
                        not set
                      </span>
                    )}
                  </div>
                  <div className="whurl">{c.blurb}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className="pill" title="test mode configured">
                    test {inTest ? '✓' : '—'}
                  </span>
                  <span className="pill" title="prod mode configured">
                    prod {inProd ? '✓' : '—'}
                  </span>
                  {count && (
                    <span className="pill indigo" title="24h · 7d · total">
                      {count['24h']} · {count['7d']} · {count.total}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.link className="ic" />
          <h3>Routing</h3>
        </div>
        <div className="body pad">
          <div className="muted" style={{ fontSize: 12.5 }}>
            Each scraper has a category default and can receive an encrypted profile-specific override through its routing API.
            The active profile resolves override first, then category. Every posted raffle additionally gets the global{' '}
            <code className="mono-sm">#layout</code> helper message.
          </div>
        </div>
      </div>
    </>
  );
}
